import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { getPesticideList, getSendLog } from "../context/api_alimtalk";

// ── 공통 스타일 ──────────────────────────────────────────────
const PanelTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;

  th, td {
    padding: 7px 9px;
    border: 1px solid #ddd;
    text-align: center;
    white-space: nowrap;
  }
  th {
    background: #f0f0f0;
    font-weight: bold;
  }
  tr:nth-child(even) td { background: #fafafa; }
`;

const PanelTitle = styled.div`
  font-size: 14px;
  font-weight: bold;
  padding: 8px 10px;
  background: #eef3fb;
  border-bottom: 2px solid #4a90d9;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-shrink: 0;
`;

const SectionLabel = styled.span`
  font-size: 11px;
  color: #4a90d9;
  border: 1px solid #4a90d9;
  border-radius: 3px;
  padding: 1px 5px;
  margin-right: 4px;
`;

const IndexBadge = styled.span`
  display: inline-block;
  background: #4a90d9;
  color: #fff;
  font-size: 11px;
  font-weight: bold;
  padding: 1px 7px;
  border-radius: 10px;
  margin-right: 4px;
`;

const ScrollArea = styled.div`
  overflow-y: auto;
  flex: 1;
  min-height: 0;
  padding: 0 10px 8px;
`;

const EmptyText = styled.p`
  color: #aaa;
  font-size: 13px;
  text-align: center;
  padding: 16px 0;
`;

const SpotInfoBox = styled.div`
  background: #f5f8ff;
  border: 1px solid #c0d4f5;
  border-radius: 5px;
  padding: 8px 10px;
  margin: 8px 0 4px;
  font-size: 12px;

  .spot-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 4px;
    font-weight: bold;
    font-size: 13px;
  }
  .close-btn {
    background: none;
    border: none;
    cursor: pointer;
    font-size: 15px;
    color: #888;
    line-height: 1;
    &:hover { color: #333; }
  }
  .spot-detail {
    display: flex;
    gap: 10px;
    flex-wrap: wrap;
    color: #555;
  }
  .badge {
    display: inline-block;
    padding: 1px 7px;
    border-radius: 8px;
    font-size: 11px;
    font-weight: bold;
    color: #fff;
  }
`;

// ── 패널 레이아웃 ─────────────────────────────────────────────
const Panel = styled.div`
  display: flex;
  flex-direction: column;
  overflow: hidden;
  flex: 1 1 0;
  min-height: 0;
  border-bottom: ${({ noBorder }) => noBorder ? "none" : "3px solid #c8d8ee"};
`;

// ── groupBy 헬퍼 ─────────────────────────────────────────────
const groupByIndex = (data) =>
  data.reduce((acc, item) => {
    const key = item.index ?? "-";
    if (!acc[key]) acc[key] = [];
    acc[key].push(item);
    return acc;
  }, {});

// ── 컴포넌트 ─────────────────────────────────────────────────
const AlimtalkLeftSideComponent = ({
  setLoading,
  setError,
  targetCrop,
  targetYear,
  targetRegion,
  selectedSpot,
  setSelectedSpot,
}) => {
  const [pesticideData, setPesticideData] = useState([]);
  const [regionSendLog, setRegionSendLog] = useState([]);
  const [stationSendLog, setStationSendLog] = useState([]);

  // 지점 선택시 해당 지점 region, 아니면 targetRegion
  const activeRegion = selectedSpot
    ? { region: selectedSpot.region, regionDesc: selectedSpot.regionName ?? selectedSpot.region }
    : targetRegion;

  const isAllRegion = !activeRegion || activeRegion.region === "all";

  useEffect(() => {
    if (!targetCrop || !targetYear) return;

    const regionToUse = selectedSpot
      ? { region: selectedSpot.region, regionDesc: selectedSpot.regionName ?? selectedSpot.region }
      : targetRegion;

    const isAll = !regionToUse || regionToUse.region === "all";

    // 약제 + 지역 발송 로그
    if (isAll) {
      setPesticideData([]);
      setRegionSendLog([]);
    } else {
      getPesticideList(
        (data) => setPesticideData(data),
        (val) => setLoading(val),
        (e) => setError(e),
        targetCrop,
        targetYear,
        regionToUse
      );
      getSendLog(
        (data) => setRegionSendLog(data),
        () => {},
        (e) => setError(e),
        targetCrop,
        targetYear,
        regionToUse,
        null
      );
    }

    // 지점 발송 내역
    if (!selectedSpot) {
      setStationSendLog([]);
    } else {
      getSendLog(
        (data) => setStationSendLog(data),
        () => {},
        (e) => setError(e),
        targetCrop,
        targetYear,
        null,
        selectedSpot.st
      );
    }
  }, [targetCrop, targetYear, targetRegion, selectedSpot]);

  const grouped = groupByIndex(pesticideData);
  const regionLabel = activeRegion?.regionDesc ?? "전체";

  const SendLogTable = ({ data }) => (
    <PanelTable>
      <thead>
        <tr>
          <th>차수</th>
          <th>발송일</th>
          <th>당일</th>
          <th>누적</th>
          <th>전체농가</th>
          <th>누적비율</th>
        </tr>
      </thead>
      <tbody>
        {data.map((log, idx) => (
          <tr key={idx}>
            <td><IndexBadge style={{ background: "#5a9e6f" }}>{log.index}차</IndexBadge></td>
            <td>{log.sendDt ?? "-"}</td>
            <td>{log.todaySendCount ?? "-"}</td>
            <td>{log.accSendCount ?? "-"}</td>
            <td>{log.totFarmerCount ?? "-"}</td>
            <td>{log.accSendPercent != null ? `${log.accSendPercent}%` : "-"}</td>
          </tr>
        ))}
      </tbody>
    </PanelTable>
  );

  return (
    <div className="container left" style={{ display: "flex", flexDirection: "column", height: "100%", overflow: "hidden" }}>
      <h1 className="title-top" style={{ flexShrink: 0, padding: "6px 10px", margin: 0, fontSize: "0.9em" }}>전국 과수화상병 알림톡 모니터링</h1>

      {/* ── 약제 정보 ── */}
      <Panel flex={2}>
        <PanelTitle>
          <span><SectionLabel>약제</SectionLabel>{targetCrop?.titleKo ?? "-"} / {targetYear ?? "-"} / {regionLabel}</span>
        </PanelTitle>
        <ScrollArea>
          {pesticideData.length === 0 ? (
            <EmptyText>시군을 선택하면 약제 정보가 표시됩니다.</EmptyText>
          ) : (
            Object.entries(grouped)
              .sort(([a], [b]) => Number(a) - Number(b))
              .map(([index, items]) => (
                <div key={index} style={{ marginTop: 8 }}>
                  <div style={{ fontSize: 12, fontWeight: "bold", marginBottom: 4, color: "#333" }}>
                    <IndexBadge>{index}차</IndexBadge>
                    {items[0]?.pesticideClass ?? ""}
                  </div>
                  <PanelTable>
                    <thead>
                      <tr>
                        <th>지역</th>
                        <th>구분</th>
                        <th>약제명</th>
                      </tr>
                    </thead>
                    <tbody>
                      {items.map((item, idx) => (
                        <tr key={idx}>
                          <td>{item.regionName}</td>
                          <td>{item.type}</td>
                          <td>{item.pesticide}</td>
                        </tr>
                      ))}
                    </tbody>
                  </PanelTable>
                </div>
              ))
          )}
        </ScrollArea>
      </Panel>

      {/* ── 지역별 발송 로그 ── */}
      <Panel flex={2}>
        <PanelTitle>
          <span><SectionLabel>발송</SectionLabel>지역별 발송 로그 / {regionLabel}</span>
        </PanelTitle>
        <ScrollArea>
          {regionSendLog.length === 0 ? (
            <EmptyText>시군을 선택하면 발송 로그가 표시됩니다.</EmptyText>
          ) : (
            <SendLogTable data={regionSendLog} />
          )}
        </ScrollArea>
      </Panel>

      {/* ── 지점별 발송 내역 (지점 선택시) ── */}
      <Panel flex={1} noBorder>
        <PanelTitle>
          <span><SectionLabel>지점</SectionLabel>지점별 발송 내역</span>
          {selectedSpot && (
            <button
              onClick={() => setSelectedSpot(null)}
              style={{ background: "none", border: "none", cursor: "pointer", fontSize: 14, color: "#888" }}
            >
              ✕ 선택해제
            </button>
          )}
        </PanelTitle>

        {selectedSpot && (
          <div style={{ padding: "0 10px", flexShrink: 0 }}>
            <SpotInfoBox>
              <div className="spot-header">
                <span>
                  {selectedSpot.name ?? "-"} (지역: {selectedSpot.regionName ?? "-"}/코드: {selectedSpot.code ?? "-"})
                </span>
              </div>
              <div className="spot-detail">
                <span>
                  <span className="badge" style={{ background: selectedSpot.isSame?.color ?? "#ccc" }}>
                    총 발송차수: {selectedSpot.total ?? "-"}차 / 현재 발송차수: {selectedSpot.now ?? "-"}차
                  </span>
                </span>
              </div>
            </SpotInfoBox>
          </div>
        )}

        <ScrollArea>
          {!selectedSpot ? (
            <EmptyText>지점을 선택하면 발송 내역이 표시됩니다.</EmptyText>
          ) : stationSendLog.length === 0 ? (
            <EmptyText>발송 내역이 없습니다.</EmptyText>
          ) : (
            <SendLogTable data={stationSendLog} />
          )}
        </ScrollArea>
      </Panel>
    </div>
  );
};

export default AlimtalkLeftSideComponent;
